import { useState } from "react";
import { toast } from "sonner";
import {
    ArrowRight,
    Briefcase,
    CalendarRange,
    Check,
    Clock,
    Inbox,
    Mail,
    MessageCircle,
    MousePointerClick,
    FileText,
    ReceiptText,
    UserCheck,
    Users,
    Wallet,
} from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { QICK_APP_REGISTER_URL } from "@/lib/routes";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const QICK_LOGO = "/brand/qick-logo.png";

const scrollToId = (id) => {
    const el = document.querySelector(id);
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (el) el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
};

const FEATURES = [
    { icon: Briefcase, title: "Klanten & klussen", body: "Leg aanvragen, werkadressen, statussen en afspraken vast bij de juiste klant." },
    { icon: CalendarRange, title: "Planning & roosters", body: "Plan klussen, diensten, medewerkers en ZZP'ers zonder losse agenda's of Excel-lijsten." },
    { icon: ReceiptText, title: "Offertes & werkbonnen", body: "Maak offertes, laat werk afronden met werkbonnen en houd opvolging overzichtelijk." },
    { icon: Clock, title: "Uren & facturen", body: "Registreer uren en zet afgerond werk sneller om naar een duidelijke factuur." },
    { icon: Users, title: "Teams & flex", body: "Werk met vaste medewerkers, ZZP'ers en flexwerkers in dezelfde operationele flow." },
];

const WORKFLOW_STEPS = [
    { title: "Aanvraag", body: "Klant, adres en opdracht direct vastgelegd." },
    { title: "Offerte", body: "Maken, akkoord, klaar voor uitvoering." },
    { title: "Planning", body: "Medewerkers, ZZP'ers en roosters inplannen." },
    { title: "Factuur", body: "Vanuit offerte, uren en materialen." },
];

const SAVINGS_CARDS = [
    {
        icon: Clock,
        title: "Minder administratie",
        body: (
            <>
                Uren, klussen, kosten en facturen sluiten op elkaar aan, zodat je <strong className="font-semibold text-[#0B1528]">minder hoeft over te typen</strong>.
            </>
        ),
    },
    {
        icon: ReceiptText,
        title: "Sneller factureren",
        body: (
            <>
                Zet afgerond werk <strong className="font-semibold text-[#0B1528]">sneller om naar een duidelijke factuur</strong> vanuit dezelfde workflow.
            </>
        ),
    },
    {
        icon: CalendarRange,
        title: "Grip op marge",
        body: (
            <>
                Zie beter <strong className="font-semibold text-[#0B1528]">wat een opdracht oplevert</strong> na uren, ZZP-kosten, materialen en extra kosten.
            </>
        ),
    },
];

const WORK_CONNECT_CARDS = [
    {
        icon: Briefcase,
        title: "QICK Work voor bedrijven",
        body: "Plaats opdrachten of shifts en vind beschikbare ZZP'ers en flexwerkers.",
        cta: "Start met QICK Work",
    },
    {
        icon: Users,
        title: "QICK Connect voor ZZP'ers/flexwerkers",
        body: (
            <>
                Reageer gratis op klussen en koppel uren en facturen aan het werk. <strong className="font-semibold text-[#0B1528]">QICK! neemt geen percentage van het uurtarief</strong>.
            </>
        ),
        cta: "Reageer via QICK Connect",
    },
];

const ROLES = [
    { icon: Briefcase, title: "Eigenaar", body: "Volledig overzicht over het bedrijf." },
    { icon: CalendarRange, title: "Planner", body: "Plant klussen, roosters en medewerkers." },
    { icon: Wallet, title: "Financiën", body: "Houdt facturen, uren en kosten bij." },
    { icon: Users, title: "Medewerkers", body: "Eigen planning, uren en werkbonnen." },
    { icon: UserCheck, title: "ZZP'ers", body: "Eigen opdrachten en facturen via QICK Connect." },
];

const ADAPTATION_POINTS = [
    "Alleen de modules die je gebruikt",
    "Later altijd aan te passen",
    "Van ZZP'er tot groeiend team",
];

const INDUSTRY_FLOWS = [
    {
        label: "Installatie & techniek",
        intro: "Voor loodgieters, installateurs en elektriciens die snel schakelen tussen klussen en facturen.",
        features: [
            "Klussen plannen",
            "Personeel & ZZP'ers inzetten",
            "Offertes maken",
            "Werkbonnen afronden",
            "Uren registreren",
            "Facturen sturen",
            "Kosten & marge bekijken",
        ],
    },
    {
        label: "Bouw, afbouw & onderhoud",
        intro: "Voor timmerbedrijven, aannemers, schilders, dakdekkers, hoveniers en projectmatige uitvoerders.",
        features: [
            "Projecten en werkadressen",
            "Planning per dag",
            "Materiaal- en extra kosten",
            "Offertes naar factuur",
            "Werkbonnen",
            "Urencontrole",
            "Klantoverzicht",
        ],
    },
    {
        label: "Schoonmaak & facilitair",
        intro: "Voor schoonmaak, onderhoud en terugkerende locaties met vaste klanten en duidelijke roosters.",
        features: [
            "Terugkerende planning",
            "Locaties en vaste klanten",
            "Medewerkersroosters",
            "Checklists",
            "Urenregistratie",
            "Meldingen",
            "Facturatie",
        ],
    },
    {
        label: "Horeca & events",
        intro: "Voor horeca, catering en evenementen waar planning, beschikbaarheid en uren snel moeten kloppen.",
        features: [
            "Flexibele planning",
            "Personeel en ZZP'ers",
            "Beschikbaarheid",
            "Uren goedkeuren",
            "Inkomende ZZP-facturen",
            "Kostenoverzicht",
            "Snel schakelen",
        ],
    },
    {
        label: "Beveiliging",
        intro: "Voor beveiliging en diensten op locatie met heldere roosters, rapportages en facturatie.",
        features: [
            "Diensten plannen",
            "Locaties",
            "Medewerkersrooster",
            "Beschikbaarheid",
            "Urenregistratie",
            "Werkrapportage",
            "Facturatie",
        ],
    },
    {
        label: "ZZP & flexwerk",
        intro: "Voor zelfstandigen en flexwerkers via QICK Connect met duidelijke opdrachtinformatie.",
        features: [
            "Opdrachten ontvangen",
            "Uren indienen",
            "QICK Connect",
            "Conceptfacturen",
            "Duidelijke opdrachtinformatie",
            "Geen gedoe met losse apps",
        ],
    },
];

const PAIN_POINTS = [
    {
        icon: MessageCircle,
        title: "Aanvragen raken kwijt",
        body: "Een klant appt, een ander mailt — zonder overzicht mis je aanvragen.",
    },
    {
        icon: ReceiptText,
        title: "Handmatig overtypen kost tijd",
        body: "Gegevens overtypen uit WhatsApp of mail kost tijd tussen de klussen door.",
    },
    {
        icon: Clock,
        title: "Planning en administratie lopen niet gelijk",
        body: "Plannen, uren en facturen zitten in aparte apps.",
    },
    {
        icon: Users,
        title: "Geen overzicht in team en ZZP'ers",
        body: "Wie beschikbaar is en wie nog moet factureren blijft onduidelijk.",
    },
];

const INBOX_POINTS = [
    {
        icon: Inbox,
        title: "Mail en WhatsApp in één inbox",
        body: "Blijven gescheiden, maar staan op één plek.",
    },
    {
        icon: MousePointerClick,
        title: "Klant of klus in 1 klik",
        body: "Nieuwe aanvraag? Direct een klant of klus, zonder overtypen.",
    },
    {
        icon: ArrowRight,
        title: "Of koppel aan bestaand werk",
        body: "Hoort het al bij een klant of klus? Koppel het direct.",
    },
];

const PRICING = [
    {
        name: "Basic",
        audience: "Planningsbasis",
        price: "€12",
        unit: "per gebruiker / maand",
        note: "Voor een simpele planningsbasis.",
        points: ["Eenvoudige planning", "Interne gebruikers", "Kleine teams"],
    },
    {
        name: "Advanced Solo",
        audience: "1 gebruiker",
        price: "€19",
        unit: "totaal / maand",
        note: "Voor 1 gebruiker.",
        points: ["Complete Advanced-flow", "Planning, uren en facturen", "QICK Work & Connect"],
    },
    {
        name: "Advanced Small Team",
        audience: "3 gebruikers",
        price: "€49",
        unit: "totaal / maand",
        note: "€16,33 per gebruiker",
        helper: "Vast teamtarief",
        points: ["Complete Advanced-flow", "Planning, uren en facturen", "QICK Work & Connect"],
        featured: true,
    },
    {
        name: "Advanced Standard",
        audience: "4–9 gebruikers",
        prefix: "vanaf",
        price: "€76",
        unit: "totaal / maand",
        note: "4–9 gebruikers",
        helper: "€19 per gebruiker",
        detail: "Vanafprijs bij 4 gebruikers",
        points: ["Per gebruiker afgerekend", "Planning, uren en facturen", "Marge-inzicht"],
    },
    {
        name: "Corporate",
        audience: "10+ gebruikers",
        prefix: "vanaf",
        price: "€140",
        unit: "totaal / maand",
        note: "10+ gebruikers",
        helper: "vanaf €14 per gebruiker",
        points: ["Volumetarief", "Voor grotere teams", "Schaalbare teaminrichting"],
    },
];

const SectionHeader = ({ eyebrow, title, body, center = false }) => (
    <Reveal className={center ? "mx-auto max-w-3xl text-center" : "max-w-3xl"}>
        {eyebrow && (
            <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                {eyebrow}
            </div>
        )}
        <h2 className="text-3xl font-extrabold leading-[1.08] text-[#0B1528] md:text-4xl">
            {title}
        </h2>
        {body && (
            <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                {body}
            </p>
        )}
    </Reveal>
);

const HERO_VISUAL_INPUTS = [
    { icon: Mail, label: "Mail" },
    { icon: MessageCircle, label: "WhatsApp" },
];

const HERO_VISUAL_OUTPUTS = [
    { icon: FileText, label: "Offertes" },
    { icon: CalendarRange, label: "Planning" },
    { icon: Users, label: "Personeel" },
    { icon: ReceiptText, label: "Facturen" },
];

const HeroVisual = () => (
    <div className="motion-card motion-card-soft rounded-[28px] border border-white/20 bg-white/12 p-4 text-white shadow-[0_28px_80px_-38px_rgba(0,0,0,0.65)] backdrop-blur-xl">
        <div className="rounded-[24px] bg-white px-6 py-9 text-[#0B1528] shadow-[0_20px_60px_-34px_rgba(0,0,0,0.4)]">
            <div className="flex items-center justify-center gap-2.5">
                <div className="flex flex-col gap-2.5">
                    {HERO_VISUAL_INPUTS.map(({ icon: Icon, label }) => (
                        <div key={label} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 shadow-[0_8px_20px_-14px_rgba(15,23,42,0.5)]">
                            <Icon className="h-4 w-4 text-slate-400" />
                            <span className="text-xs font-bold text-slate-600">{label}</span>
                        </div>
                    ))}
                </div>

                <div className="h-px w-6 shrink-0 bg-gradient-to-r from-slate-200 to-blue-200 sm:w-10" />

                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#0251B7] text-xl font-extrabold text-white shadow-[0_16px_32px_-14px_rgba(2,81,183,0.65)]">
                    Q!
                </div>

                <div className="h-px w-6 shrink-0 bg-gradient-to-r from-blue-200 to-slate-200 sm:w-10" />

                <div className="flex flex-col gap-2.5">
                    {HERO_VISUAL_OUTPUTS.map(({ icon: Icon, label }) => (
                        <div key={label} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 shadow-[0_8px_20px_-14px_rgba(15,23,42,0.5)]">
                            <Icon className="h-4 w-4 text-[#0251B7]" />
                            <span className="text-xs font-bold text-slate-600">{label}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mx-auto mt-8 max-w-xs border-t border-slate-100 pt-6 text-center">
                <div className="text-xl font-extrabold text-[#0B1528]">Eén flow, geen overtypen</div>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                    Alles blijft gekoppeld aan dezelfde klus.
                </p>
            </div>
        </div>
    </div>
);

const Hero = () => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative isolate overflow-hidden bg-[#0251B7] pt-24 pb-14 md:pt-32 md:pb-20"
    >
        <div className="absolute inset-0 bg-[linear-gradient(135deg,#0251B7_0%,#064AA4_58%,#0B1528_100%)]" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white" />
        <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 overflow-hidden px-4 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8">
            <div className="min-w-0 text-center lg:text-left">
                <div className="hero-entrance hero-entrance-badge mb-5 flex justify-center lg:justify-start">
                    <span className="inline-flex h-16 w-16 items-center justify-center overflow-hidden rounded-[18px] bg-[#0251B7] shadow-[0_14px_28px_rgba(0,0,0,0.18)] ring-1 ring-white/18">
                        <img src={QICK_LOGO} alt="QICK!" className="h-[4.5rem] w-[4.5rem] rounded-[18px] object-cover" />
                    </span>
                </div>
                <h1 className="hero-entrance hero-entrance-headline mx-auto max-w-[23rem] break-words text-[2rem] font-extrabold leading-[1.1] text-white [overflow-wrap:anywhere] sm:max-w-3xl sm:text-4xl sm:leading-[1.08] md:text-5xl lg:mx-0">
                    QICK! brengt planning, uren, offertes en facturen samen.
                </h1>
                <p className="hero-entrance hero-entrance-subtitle mx-auto mt-6 max-w-[21rem] text-base leading-relaxed text-blue-50 md:max-w-2xl md:text-lg lg:mx-0">
                    Mail en WhatsApp komen samen in QICK! inbox. Koppel in 1 klik aan een klant of klus, en plan en factureer zonder overtypen.
                </p>
                <ul className="hero-entrance hero-entrance-trust mx-auto mt-6 grid max-w-[23rem] gap-3 text-left text-sm font-semibold text-white md:max-w-2xl md:grid-cols-3 lg:mx-0">
                    {[
                        "Klant of klus in 1 klik uit mail & WhatsApp",
                        "Personeel, ZZP'ers en roosters plannen",
                        "Uren, werkbonnen en facturen",
                    ].map((item) => (
                        <li key={item} className="flex items-start gap-2 rounded-2xl bg-white/10 p-3 ring-1 ring-white/15">
                            <Check className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={3} />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
                <div className="hero-entrance hero-entrance-ctas mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center lg:justify-start">
                    <Button
                        asChild
                        data-testid="hero-start-trial-btn"
                        className="motion-button h-12 w-full rounded-full bg-white px-7 font-semibold text-[#0251B7] shadow-[0_16px_34px_-16px_rgba(0,26,67,0.55)] hover:bg-blue-50 hover:text-[#023D8A] hover:shadow-[0_20px_42px_-18px_rgba(0,26,67,0.62)] sm:w-auto"
                    >
                        <a href={QICK_APP_REGISTER_URL}>
                            Start gratis als bedrijf
                            <ArrowRight className="ml-1.5 h-4 w-4" />
                        </a>
                    </Button>
                    <Button
                        data-testid="hero-view-features-btn"
                        variant="outline"
                        onClick={() => scrollToId("#hoe-werkt-het")}
                        className="motion-button h-12 w-full rounded-full border-white/30 bg-white/10 px-7 font-semibold text-white backdrop-blur hover:bg-white/18 hover:text-white hover:shadow-[0_18px_38px_-24px_rgba(255,255,255,0.45)] sm:w-auto"
                    >
                        Bekijk de workflow
                    </Button>
                </div>
            </div>
            <div className="hero-entrance hero-entrance-visual hidden lg:block">
                <HeroVisual />
            </div>
        </div>
    </section>
);

const PainPointsSection = () => (
    <section data-testid="pain-points-section" className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="Herken je dit?"
                title="Losse tools kosten je tijd en klanten"
                body="Losse agenda's, WhatsApp, mail en Excel naast elkaar kosten overzicht en tijd."
                center
            />
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {PAIN_POINTS.map((point, i) => {
                    const Icon = point.icon;
                    return (
                        <Reveal key={point.title} delay={i * 55} className="motion-card rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[#0251B7] ring-1 ring-slate-200">
                                <Icon className="h-5 w-5" />
                            </div>
                            <h3 className="mt-4 text-base font-bold text-[#0B1528]">{point.title}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600">{point.body}</p>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const InboxFeatureSection = () => (
    <section id="inbox" data-testid="inbox-feature-section" className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="motion-card motion-card-soft grid grid-cols-1 gap-8 overflow-hidden rounded-[28px] border border-blue-100 bg-[linear-gradient(135deg,#EFF6FF_0%,#FFFFFF_52%,#F8FAFC_100%)] p-5 shadow-[0_26px_74px_-42px_rgba(2,81,183,0.45)] md:p-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
                <Reveal>
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                        QICK! Inbox
                    </div>
                    <h2 className="text-3xl font-extrabold leading-[1.08] text-[#0B1528] md:text-4xl">
                        Van WhatsApp-bericht naar klant of klus in 1 klik
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
                        Mail en WhatsApp komen binnen in dezelfde inbox. Koppel aan bestaand werk, of maak er direct een klant of klus van.
                    </p>
                    <ul className="mt-6 grid grid-cols-1 gap-3">
                        {INBOX_POINTS.map((point, i) => {
                            const Icon = point.icon;
                            return (
                                <Reveal as="li" key={point.title} delay={80 + i * 55} className="flex items-start gap-3 rounded-xl bg-white p-3.5 ring-1 ring-slate-200">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-[#0251B7] ring-1 ring-blue-100">
                                        <Icon className="h-4 w-4" strokeWidth={2.5} />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-[#0B1528]">{point.title}</div>
                                        <div className="mt-0.5 text-sm leading-relaxed text-slate-600">{point.body}</div>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </ul>
                    <Button
                        asChild
                        data-testid="inbox-start-trial-btn"
                        className="motion-button mt-6 h-11 w-full rounded-full bg-[#0251B7] px-6 font-semibold text-white hover:bg-[#023D8A] hover:shadow-[0_16px_32px_-18px_rgba(2,81,183,0.72)] sm:w-auto"
                    >
                        <a href={QICK_APP_REGISTER_URL}>
                            Probeer de QICK! inbox
                            <ArrowRight className="ml-1.5 h-4 w-4" />
                        </a>
                    </Button>
                </Reveal>

                <Reveal delay={120} className="motion-card motion-card-soft rounded-[24px] border border-white/60 bg-white p-4 shadow-[0_20px_60px_-34px_rgba(2,81,183,0.4)] md:p-5">
                    <div className="rounded-2xl bg-[#F8FAFC] p-4 ring-1 ring-slate-100">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                            <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                            WhatsApp
                            <span className="text-slate-300">•</span>
                            <Mail className="h-3.5 w-3.5 text-[#0251B7]" />
                            Mail
                        </div>
                        <div className="mt-3 rounded-2xl rounded-tl-sm bg-white p-3.5 text-sm leading-relaxed text-slate-700 shadow-sm ring-1 ring-slate-100">
                            "Hoi, ik wil graag mijn kantoor laten schoonmaken, elke week. Adres: Voorbeeldstraat 12, Rotterdam."
                        </div>
                        <div className="mt-1.5 text-right text-[11px] font-semibold text-slate-400">Binnengekomen in QICK! inbox</div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-center text-xs font-bold text-slate-600">
                            Koppel aan klant/klus
                        </div>
                        <div className="flex items-center justify-center gap-1.5 rounded-xl bg-[#0251B7] px-3 py-2.5 text-center text-xs font-bold text-white">
                            <MousePointerClick className="h-3.5 w-3.5" strokeWidth={2.5} />
                            Maak klant/klus
                        </div>
                    </div>

                    <div className="mt-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-4">
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">Klus aangemaakt</div>
                            <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
                        </div>
                        <div className="mt-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                                <span className="text-slate-500">Werk</span>
                                <span className="font-semibold text-[#0B1528]">Kantoor schoonmaak</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-slate-500">Adres</span>
                                <span className="font-semibold text-[#0B1528]">Overgenomen uit bericht</span>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const FeaturesSection = () => (
    <section id="hoe-werkt-het" data-testid="features-section" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="Hoe werkt het"
                title="Van aanvraag naar factuur"
                body="Van binnenkomend werk tot betaalde factuur."
                center
            />
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-4">
                {WORKFLOW_STEPS.map((step, i) => (
                    <Reveal key={step.title} delay={i * 55} className="motion-card relative rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.45)]">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0251B7] text-sm font-extrabold text-white">
                            {i + 1}
                        </div>
                        <h3 className="mt-4 text-lg font-bold text-[#0B1528]">{step.title}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={220} className="mt-8 flex flex-wrap items-center justify-center gap-2.5">
                {FEATURES.map((feature) => {
                    const Icon = feature.icon;
                    return (
                        <span key={feature.title} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                            <Icon className="h-4 w-4 text-[#0251B7]" />
                            {feature.title}
                        </span>
                    );
                })}
            </Reveal>
            <Reveal delay={250} className="mx-auto mt-6 max-w-3xl text-center text-sm leading-relaxed text-slate-500">
                Voor servicebedrijven, bouw en techniek, horeca en beveiliging.
            </Reveal>
        </div>
    </section>
);

const SavingsSection = () => (
    <section data-testid="savings-section" className="bg-[#F8FAFC] py-8 md:py-10">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <Reveal className="motion-card motion-card-soft rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.35)] md:px-8 md:py-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="max-w-sm">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                            Tijd en kosten
                        </div>
                        <h2 className="mt-1.5 text-xl font-extrabold leading-tight text-[#0B1528] md:text-2xl">
                            Minder administratie. Meer grip op je werk.
                        </h2>
                    </div>
                    <div className="flex flex-wrap gap-2.5">
                        {SAVINGS_CARDS.map((card, i) => {
                            const Icon = card.icon;
                            return (
                                <Reveal key={card.title} delay={i * 55} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-[#F8FAFC] px-4 py-2 text-sm font-semibold text-slate-700">
                                    <Icon className="h-4 w-4 text-[#0251B7]" />
                                    {card.title}
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const DETAILED_STEPS = [
    {
        label: "Aanvraag via QICK Inbox",
        body: "Mail en WhatsApp komen samen in QICK Inbox. Koppel aan bestaand werk, of maak in 1 klik een klant of klus.",
        visual: (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-[#0251B7]">
                        <Mail className="h-3.5 w-3.5" />
                        E-mail
                    </div>
                    <div className="mt-2 text-xs text-slate-500">Nieuwe aanvraag binnengekomen</div>
                    <div className="mt-2.5 flex gap-1.5">
                        <span className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-center text-[11px] font-bold text-slate-600">
                            Koppel aan klant/klus
                        </span>
                        <span className="flex-1 rounded-md bg-[#0251B7] px-2 py-1.5 text-center text-[11px] font-bold text-white">
                            Maak klant/klus
                        </span>
                    </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-emerald-600">
                        <MessageCircle className="h-3.5 w-3.5" />
                        WhatsApp
                    </div>
                    <div className="mt-2 rounded-lg rounded-tl-sm bg-[#F8FAFC] p-2 text-xs leading-relaxed text-slate-600">
                        "Ik wil graag mijn kantoor laten schoonmaken. Adres: Voorbeeldstraat 12, Rotterdam."
                    </div>
                    <div className="mt-2.5 flex gap-1.5">
                        <span className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-center text-[11px] font-bold text-slate-600">
                            Koppel aan klant/klus
                        </span>
                        <span className="flex-1 rounded-md bg-[#0251B7] px-2 py-1.5 text-center text-[11px] font-bold text-white">
                            Maak klant/klus
                        </span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        label: "Klant & klus vastgelegd",
        body: "Klant, adres en opdracht staan meteen goed vast.",
        visual: (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold text-[#0B1528]">Nieuwe klus</div>
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-100">Vastgelegd</span>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Klant</span>
                        <span className="font-semibold text-[#0B1528]">Automatisch overgenomen</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Werkadres</span>
                        <span className="font-semibold text-[#0B1528]">Overgenomen uit bericht</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        label: "Inplannen",
        body: "Plan de klus in bij een medewerker of ZZP'er.",
        visual: (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold text-[#0B1528]">Planning</div>
                    <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[11px] font-bold text-[#0251B7] ring-1 ring-blue-100">Ingepland</span>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Toegewezen aan</span>
                        <span className="font-semibold text-[#0B1528]">Medewerker of ZZP'er</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Datum & tijd</span>
                        <span className="font-semibold text-[#0B1528]">Zichtbaar in rooster</span>
                    </div>
                </div>
            </div>
        ),
    },
    {
        label: "Uren & factuur",
        body: "Uren en afgerond werk worden direct een factuur.",
        visual: (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold text-[#0B1528]">Uren & factuur</div>
                    <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700 ring-1 ring-emerald-100">Concept klaar</span>
                </div>
                <div className="mt-3 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Uren</span>
                        <span className="font-semibold text-[#0B1528]">Overgenomen uit registratie</span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-slate-500">Factuur</span>
                        <span className="font-semibold text-[#0B1528]">Op basis van dezelfde klus</span>
                    </div>
                </div>
            </div>
        ),
    },
];

const DetailedWorkflowSection = () => (
    <section data-testid="detailed-workflow-section" className="bg-[#F8FAFC] py-14 md:py-16">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="De kern van QICK!"
                title="Zo werkt QICK! in de praktijk"
                body="Dezelfde klus doorloopt elke stap, zonder overtypen."
                center
            />
            <div className="mt-12">
                {DETAILED_STEPS.map((step, i) => (
                    <div key={step.label} className="relative flex gap-5 pb-10 last:pb-0">
                        {i < DETAILED_STEPS.length - 1 && (
                            <div aria-hidden="true" className="absolute left-[19px] top-10 bottom-0 w-px bg-blue-100" />
                        )}
                        <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0251B7] text-sm font-extrabold text-white shadow-[0_10px_24px_-14px_rgba(2,81,183,0.7)]">
                            {i + 1}
                        </div>
                        <Reveal delay={i * 70} className="motion-card motion-card-soft flex-1 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5 md:p-6">
                            <h3 className="text-lg font-bold text-[#0B1528] md:text-xl">{step.label}</h3>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600 md:text-base">{step.body}</p>
                            <div className="mt-4">{step.visual}</div>
                        </Reveal>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

const WorkSection = () => (
    <section id="qick-work" data-testid="qick-work-section" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="QICK Work + QICK Connect"
                title="Bedrijven en flexwerkers in dezelfde werkflow"
                body="Extra capaciteit en losse opdrachten, met duidelijke uren. QICK! neemt geen percentage van het uurtarief."
                center
            />
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
                {WORK_CONNECT_CARDS.map((card, i) => {
                    const Icon = card.icon;
                    return (
                        <Reveal key={card.title} delay={i * 70} className="motion-card rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0251B7] text-white">
                                <Icon className="h-5 w-5" />
                            </div>
                            <h3 className="mt-4 text-xl font-extrabold text-[#0B1528]">{card.title}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-600 md:text-base">{card.body}</p>
                            <Button
                                asChild
                                className="motion-button mt-5 h-10 w-full rounded-full bg-white px-5 font-semibold text-[#0251B7] ring-1 ring-blue-100 hover:bg-blue-50 hover:text-[#023D8A] hover:shadow-[0_14px_30px_-20px_rgba(2,81,183,0.48)] sm:w-auto"
                            >
                                <a href={QICK_APP_REGISTER_URL}>
                                    {card.cta}
                                    <ArrowRight className="ml-1.5 h-4 w-4" />
                                </a>
                            </Button>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const RolesSection = () => (
    <section data-testid="roles-section" className="bg-[#F8FAFC] py-14 md:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="Rollen & rechten"
                title="Elke rol ziet alleen wat voor hen relevant is"
                body="Iedereen werkt in dezelfde omgeving, met toegang die past bij het werk."
                center
            />
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {ROLES.map((role, i) => {
                    const Icon = role.icon;
                    return (
                        <Reveal key={role.title} delay={i * 55} className="motion-card rounded-2xl border border-slate-200 bg-white p-4 text-center">
                            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-[#0251B7] ring-1 ring-blue-100">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className="mt-3 text-sm font-bold text-[#0B1528]">{role.title}</div>
                            <div className="mt-1 text-xs leading-relaxed text-slate-500">{role.body}</div>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const AdaptationSection = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const activeFlow = INDUSTRY_FLOWS[activeIndex];

    return (
        <section id="branches" data-testid="adaptation-section" className="bg-white py-14 md:py-16">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="motion-card motion-card-soft rounded-[28px] border border-blue-100 bg-[linear-gradient(135deg,#F8FAFC_0%,#FFFFFF_45%,#EFF6FF_100%)] p-5 shadow-[0_24px_70px_-42px_rgba(2,81,183,0.4)] ring-1 ring-white/80 md:p-8">
                    <SectionHeader
                        eyebrow="Inrichting per branche"
                        title="QICK! past zich aan jouw bedrijf aan"
                        body="Zet alleen de modules aan die je nodig hebt. Kies hieronder je branche."
                        center
                    />
                    <Reveal delay={60} className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                        {ADAPTATION_POINTS.map((point) => (
                            <span key={point} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">
                                <Check className="h-4 w-4 text-[#0251B7]" strokeWidth={3} />
                                {point}
                            </span>
                        ))}
                    </Reveal>

                    <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[0.7fr_1.3fr]">
                        <Reveal delay={90} className="grid grid-cols-1 content-start gap-2 sm:grid-cols-2 lg:grid-cols-1" role="group" aria-label="Kies een branche">
                            {INDUSTRY_FLOWS.map((flow, index) => {
                                const isActive = index === activeIndex;
                                return (
                                    <button
                                        key={flow.label}
                                        type="button"
                                        aria-pressed={isActive}
                                        onClick={() => setActiveIndex(index)}
                                        className={`motion-choice rounded-xl px-3 py-2.5 text-left text-sm font-bold ring-1 ${
                                            isActive
                                                ? "bg-[#0251B7] text-white shadow-[0_12px_26px_-18px_rgba(2,81,183,0.7)] ring-[#0251B7]"
                                                : "bg-white text-slate-700 ring-slate-200 hover:bg-blue-50 hover:text-[#0251B7] hover:ring-blue-100"
                                        }`}
                                    >
                                        {flow.label}
                                    </button>
                                );
                            })}
                        </Reveal>

                        <Reveal delay={120} className="motion-card motion-card-soft min-w-0 rounded-2xl border border-blue-100 bg-white/95 p-5 shadow-[0_18px_54px_-40px_rgba(2,81,183,0.45)] md:p-6">
                            <div key={activeFlow.label} className="industry-flow-panel">
                                <div className="flex flex-col gap-3 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
                                    <h3 className="text-xl font-extrabold leading-tight text-[#0B1528]">
                                        {activeFlow.label}
                                    </h3>
                                    <div className="w-fit rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 ring-1 ring-emerald-100">
                                        Relevante functies
                                    </div>
                                </div>
                                <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">
                                    {activeFlow.intro}
                                </p>
                                <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {activeFlow.features.map((feature) => (
                                        <div key={feature} className="flex min-h-12 items-start gap-3 rounded-xl bg-[#F8FAFC] p-3 text-sm font-semibold text-slate-700 ring-1 ring-slate-200">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0251B7]" strokeWidth={3} />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};

const PricingSection = () => (
    <section id="pricing" data-testid="pricing-section" className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                eyebrow="Prijzen"
                title="Duidelijke prijzen"
                body="Kies het pakket dat past bij je team. Jaarbetaling geeft 17% korting."
                center
            />
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                {PRICING.map((tier, i) => (
                    <Reveal
                        key={tier.name}
                        delay={i * 55}
                        className={`motion-card flex h-full flex-col rounded-2xl border p-5 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.45)] ${
                            tier.featured ? "border-[#0251B7] bg-white ring-2 ring-[#0251B7]/10" : "border-slate-200 bg-white"
                        }`}
                    >
                        {tier.featured && (
                            <div className="mb-3 w-fit rounded-full bg-[#0251B7] px-3 py-1 text-xs font-bold text-white">
                                Populair
                            </div>
                        )}
                        <h3 className="text-lg font-extrabold text-[#0B1528]">{tier.name}</h3>
                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[#0251B7]">{tier.audience}</p>
                        <div className="mt-4">
                            {tier.prefix && (
                                <span className="mr-2 align-baseline text-sm font-bold tracking-[0.08em] text-slate-500">
                                    {tier.prefix}
                                </span>
                            )}
                            <span className="text-4xl font-extrabold text-[#0B1528]">{tier.price}</span>
                            <span className="ml-1 inline-block max-w-[8.75rem] align-baseline text-xs font-semibold leading-snug text-slate-500">
                                {tier.unit}
                            </span>
                        </div>
                        <p className="mt-3 min-h-[3rem] text-sm leading-relaxed text-slate-600">{tier.note}</p>
                        {tier.helper && (
                            <p className="mt-2 text-sm font-semibold leading-snug text-[#0B1528]">{tier.helper}</p>
                        )}
                        {tier.detail && (
                            <p className="mt-1 text-xs font-semibold leading-snug text-slate-500">{tier.detail}</p>
                        )}
                        <ul className="mt-5 flex-1 space-y-3">
                            {tier.points.map((point) => (
                                <li key={point} className="flex items-start gap-2 text-sm text-slate-700">
                                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0251B7]" strokeWidth={3} />
                                    <span>{point}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={170} className="mx-auto mt-5 max-w-4xl text-center text-xs leading-relaxed text-slate-500 md:text-sm">
                Prijzen gelden voor interne bedrijfsgebruikers. QICK Connect-toegang voor ZZP'ers/flexwerkers telt niet als gewone medewerker-seat en <strong className="font-semibold text-slate-700">QICK! neemt geen percentage van hun uurtarief</strong>.
            </Reveal>
            <Reveal delay={172} className="mx-auto mt-3 max-w-4xl text-center text-xs leading-relaxed text-slate-500 md:text-sm">
                Nederlandse software met juridische documenten en AVG-documentatie beschikbaar via de footer.
            </Reveal>
            <Reveal delay={175} className="motion-card motion-card-soft mx-auto mt-6 max-w-5xl rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_40px_-34px_rgba(15,23,42,0.45)] md:p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[0.8fr_1.2fr] md:items-center">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                            Overstappen
                        </div>
                        <h3 className="mt-2 text-2xl font-extrabold text-[#0B1528]">
                            Praktische overstaphulp
                        </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 md:text-base">
                        Stap je over vanuit Excel, losse agenda's of een ander systeem? We helpen je met klanten, lopende klussen en basisgegevens, <strong className="font-semibold text-[#0B1528]">zodat je sneller met QICK! kunt starten</strong>.
                    </p>
                </div>
            </Reveal>
            <Reveal delay={180} className="motion-card motion-card-soft mt-6 rounded-2xl border border-blue-100 bg-white p-5 md:p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-[0.8fr_1.2fr] md:items-center">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                            QICK Connect
                        </div>
                        <h3 className="mt-2 text-2xl font-extrabold text-[#0B1528]">
                            Gratis reageren en inschrijven op klussen
                        </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-600 md:text-base">
                        Met QICK Connect kunnen flexwerkers en ZZP'ers gratis reageren op klussen en opdrachten. Bedrijven betalen voor QICK! als SaaS-platform. <span className="font-semibold text-[#0B1528]">Jij houdt je volledige uurtarief:</span> QICK! neemt geen percentage.
                    </p>
                </div>
            </Reveal>
        </div>
    </section>
);

const BottomCTA = () => (
    <section data-testid="bottom-cta-section" className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Reveal className="motion-card motion-card-soft rounded-[28px] bg-[#0251B7] p-8 text-center text-white shadow-[0_26px_70px_-34px_rgba(2,81,183,0.55)] md:p-12">
                <h2 className="text-3xl font-extrabold leading-[1.08] md:text-4xl">
                    Klaar om QICK! te proberen?
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-blue-50 md:text-lg">
                    Start zelf, of neem contact op voor meer info.
                </p>
                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
                    <Button
                        asChild
                        data-testid="cta-start-trial-btn"
                        className="motion-button h-12 w-full rounded-full bg-white px-7 font-semibold text-[#0251B7] hover:bg-blue-50 hover:text-[#023D8A] hover:shadow-[0_18px_38px_-20px_rgba(0,26,67,0.45)] sm:w-auto"
                    >
                        <a href={QICK_APP_REGISTER_URL}>
                            Start gratis als bedrijf
                            <ArrowRight className="ml-1.5 h-4 w-4" />
                        </a>
                    </Button>
                    <Button
                        type="button"
                        data-testid="cta-contact-btn"
                        variant="outline"
                        onClick={() => scrollToId("#contact")}
                        className="motion-button h-12 w-full rounded-full border-white/30 bg-white/10 px-7 font-semibold text-white hover:bg-white/18 hover:text-white hover:shadow-[0_18px_38px_-24px_rgba(255,255,255,0.45)] sm:w-auto"
                    >
                        Neem contact op
                    </Button>
                </div>
            </Reveal>
        </div>
    </section>
);

const initialForm = {
    name: "",
    companyName: "",
    email: "",
    phone: "",
    planInterest: "QICK! Platform",
    message: "",
};

const contactEndpoint = process.env.REACT_APP_CONTACT_ENDPOINT || "/api/contact";

const CONTACT_ERROR_MESSAGE =
    "Er ging iets mis bij het versturen van je aanvraag. Probeer het over een moment opnieuw.";

const ContactSection = () => {
    const [form, setForm] = useState(initialForm);
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (key) => (e) => {
        setIsSuccessOpen(false);
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handlePlanChange = (value) => {
        setIsSuccessOpen(false);
        setForm((prev) => ({ ...prev, planInterest: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const trimmedForm = {
            name: form.name.trim(),
            companyName: form.companyName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            planInterest: form.planInterest.trim(),
            message: form.message.trim(),
        };

        const requiredFields = [
            trimmedForm.name,
            trimmedForm.companyName,
            trimmedForm.email,
            trimmedForm.planInterest,
        ];

        if (requiredFields.some((value) => !value)) {
            toast.error("Vul de verplichte velden in voordat je verzendt.");
            return;
        }

        const lead = {
            ...trimmedForm,
            submittedAt: new Date().toISOString(),
            source: "qickapp.com",
        };

        setIsSubmitting(true);

        try {
            const response = await fetch(contactEndpoint, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(lead),
            });

            if (!response.ok) {
                const errorDetails = await response.json().catch(() => null);
                throw new Error(errorDetails?.error || "Contact request failed");
            }

            setForm(initialForm);
            setIsSuccessOpen(true);
        } catch (error) {
            console.error("Contact request failed", error);
            toast.error(CONTACT_ERROR_MESSAGE);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="border-t border-slate-100 bg-white py-16 md:py-20">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
                <Reveal className="lg:col-span-2">
                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-[#0251B7]">
                        Contact
                    </div>
                    <h2 className="text-3xl font-extrabold leading-[1.08] text-[#0B1528] md:text-4xl">
                        Wil je QICK! bespreken?
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-600">
                        Vertel kort wat je zoekt. We reageren snel.
                    </p>
                    <p className="mt-4 text-sm font-semibold leading-relaxed text-slate-600">
                        Liever direct mailen? contact@qickapp.com
                    </p>
                </Reveal>

                <Reveal delay={100} className="lg:col-span-3">
                    <form onSubmit={handleSubmit} data-testid="contact-form" className="motion-card motion-card-soft rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_18px_54px_-38px_rgba(15,23,42,0.35)] md:p-6">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                                <Label htmlFor="name" className="text-sm font-semibold text-slate-700">Naam</Label>
                                <Input id="name" data-testid="contact-input-name" value={form.name} onChange={handleChange("name")} className="mt-2 h-11 rounded-xl border-slate-200" required />
                            </div>
                            <div>
                                <Label htmlFor="company" className="text-sm font-semibold text-slate-700">Bedrijfsnaam</Label>
                                <Input id="company" data-testid="contact-input-company" value={form.companyName} onChange={handleChange("companyName")} className="mt-2 h-11 rounded-xl border-slate-200" required />
                            </div>
                            <div>
                                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">E-mail</Label>
                                <Input id="email" data-testid="contact-input-email" type="email" value={form.email} onChange={handleChange("email")} className="mt-2 h-11 rounded-xl border-slate-200" required />
                            </div>
                            <div>
                                <Label htmlFor="phone" className="text-sm font-semibold text-slate-700">Telefoon <span className="font-normal text-slate-400">(optioneel)</span></Label>
                                <Input id="phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={handleChange("phone")} className="mt-2 h-11 rounded-xl border-slate-200" />
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="planInterest" className="text-sm font-semibold text-slate-700">Interesse</Label>
                                <Select value={form.planInterest} onValueChange={handlePlanChange} required>
                                    <SelectTrigger id="planInterest" data-testid="contact-select-plan" className="mt-2 h-11 rounded-xl border-slate-200">
                                        <SelectValue placeholder="Kies een optie" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="QICK! Platform">QICK! Platform</SelectItem>
                                        <SelectItem value="QICK Work">QICK Work</SelectItem>
                                        <SelectItem value="QICK Connect / ZZP">QICK Connect / ZZP</SelectItem>
                                        <SelectItem value="Korte demo">Korte demo</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="md:col-span-2">
                                <Label htmlFor="message" className="text-sm font-semibold text-slate-700">Bericht</Label>
                                <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={handleChange("message")} rows={4} className="mt-2 rounded-xl border-slate-200" />
                            </div>
                        </div>

                        <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-slate-500">Door te verzenden mag QICK! contact met je opnemen.</p>
                            <Button type="submit" data-testid="contact-submit-btn" disabled={isSubmitting} className="motion-button h-11 w-full rounded-full bg-[#0251B7] px-7 font-semibold text-white hover:bg-[#023D8A] hover:shadow-[0_16px_32px_-18px_rgba(2,81,183,0.72)] disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto">
                                {isSubmitting ? "Verzenden..." : "Neem contact op"}
                                <ArrowRight className="ml-1.5 h-4 w-4" />
                            </Button>
                        </div>
                    </form>
                </Reveal>
            </div>
            <Dialog open={isSuccessOpen} onOpenChange={setIsSuccessOpen}>
                <DialogContent className="w-[calc(100vw-2rem)] max-w-md rounded-3xl border-slate-200 p-0 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.35)]">
                    <div data-testid="contact-success-message">
                        <DialogHeader className="border-b border-slate-100 bg-[#F8FAFC] px-6 py-6 text-left">
                            <DialogTitle className="text-2xl text-[#0B1528]">Aanvraag ontvangen</DialogTitle>
                            <DialogDescription className="text-base font-bold text-[#0251B7]">Bedankt voor je bericht</DialogDescription>
                        </DialogHeader>
                        <div className="px-6 py-6 text-sm leading-relaxed text-slate-600">
                            We reageren naar verwachting binnen 1-2 werkdagen.
                        </div>
                        <DialogFooter className="border-t border-slate-100 px-6 py-4">
                            <Button type="button" onClick={() => setIsSuccessOpen(false)} className="motion-button h-11 rounded-full bg-[#0251B7] px-7 font-semibold text-white hover:bg-[#023D8A]">
                                Sluiten
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    );
};

const Landing = () => {
    return (
        <div data-testid="landing-page" className="min-h-screen w-full max-w-full overflow-x-hidden bg-white text-slate-900">
            <Navbar />
            <main>
                <Hero />
                <PainPointsSection />
                <InboxFeatureSection />
                <FeaturesSection />
                <DetailedWorkflowSection />
                <WorkSection />
                <RolesSection />
                <AdaptationSection />
                <SavingsSection />
                <PricingSection />
                <BottomCTA />
                <ContactSection />
            </main>
            <Footer />
        </div>
    );
};

export default Landing;
